import { apiBase } from "./platform/api-base";
import { InstallHint } from "./components/InstallHint";
import { SupportForm } from "./components/SupportForm";
import { SharedView } from "./views/SharedView";
import { CalendarView } from "./views/CalendarView";
import { UndoToast } from "./components/UndoToast";
import { cloneProject, removeProject } from "./domain/project";
import { flushAggregateEvents, flushAnalytics, recordFirstValue, sendAggregateEvent } from "./platform/analytics";
import { BottomTabs } from "./components/BottomTabs";
import { ProjectSwitcher } from "./components/ProjectSwitcher";
import { isAppsInTossHost } from "./platform/apps-in-toss";
import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { activeProject, createManualProject, createProjectWardrobe, toggleTask } from "./domain/project";
import { deriveProjectCommand, } from "./domain/project-command";
import type {
  AppData,
  CosplayProject,
} from "./domain/types";
import { deleteAppData, loadAppData, readAppData, saveAppData, } from "./platform/storage";
import { StorageRecovery, downloadOriginal } from "./StorageRecovery";
import { TossStorageRecovery } from "./TossStorageRecovery";
import { createAnalyticsEvent, enqueueAnalytics, parseAttribution } from "./platform/analytics";
import { getTossStorageState, subscribeTossStorage, resolveTossStorageConflict, installAppsInTossBackHandler } from "./platform/apps-in-toss";

import { usePathname, useOnlineStatus, AppLink, } from "./components/shared";
import { HomeView } from "./views/HomeView";
import { DiscoverView } from "./views/DiscoverView";
import { ProjectView } from "./views/ProjectView";
import { WardrobeView } from "./views/WardrobeView";
import { MarketView } from "./views/MarketView";
import { EventsView } from "./views/EventsView";
import { ShootView } from "./views/ShootView";
import { PhotosView } from "./views/PhotosView";
import { PortfolioView } from "./views/PortfolioView";
import { PlanView } from "./views/PlanView";
import { DataSettingsView } from "./views/DataSettingsView";
import { LegalView } from "./views/LegalView";
import { DiscoveryLandingView, discoveryLandings } from "./views/DiscoveryLandingView";
import { NotFoundView } from "./views/NotFoundView";
function PreparationHandoff({ data, pathname, navigate }: { data: AppData; pathname: string; navigate: (to: string) => void }) {
  const project = activeProject(data);
  if (!project || !["/wardrobe", "/events", "/shoot", "/photos"].includes(pathname)) return null;
  const command = deriveProjectCommand(data, project);
  const stages = command.ledgers.filter((row) => ["wardrobe", "events", "shoot", "photos"].includes(row.id));
  return <section className="route-handoff preparation-handoff" aria-label="다음 준비 행동">
    <div><strong>{project.characterName} 준비 이어가기</strong>
      <ol className="preparation-steps">{stages.map((row) => <li key={row.id}><button type="button" className="button text" aria-current={row.route === pathname ? "step" : undefined} onClick={() => navigate(row.route)}>{row.label} · {row.evidenceCount}/{row.evidenceTotal}</button></li>)}</ol>
      <p>{command.nextAction.reason}</p>
    </div>
    <div className="data-button-row">
      {command.nextAction.route !== pathname && <button type="button" className="button primary" onClick={() => navigate(command.nextAction.route)}>{command.nextAction.label}</button>}
      {pathname === "/shoot" && data.shootPlans.some(plan => plan.projectId === project.id) && <button className="button primary" onClick={() => navigate("/photos")}>다음: 받은 사진 확인</button>}
      {pathname === "/photos" && data.photos.some(photo => photo.projectId === project.id) && <button className="button primary" onClick={() => navigate("/portfolio")}>다음: 완주 기록 확인</button>}
      <button type="button" className="button" onClick={() => navigate(`/project/${project.id}`)}>전체 준비표로</button>
    </div>
  </section>;
}

export function App() {
  const { pathname, navigate:routeNavigate } = usePathname();
  const [readState, setReadState] = useState(readAppData);
  const [data, setStoredData] = useState<AppData>(() => readState.data);
  const dataRef = useRef(data); dataRef.current = data;
  const [undo, setUndo] = useState<{ data: AppData; expiresAt: number }>();
  const setData: React.Dispatch<React.SetStateAction<AppData>> = (action) => {
    const current = dataRef.current;
    const next = typeof action === "function" ? action(current) : action;
    const collections = ["projects", "wardrobe", "shootPlans", "photos", "safetyActions"] as const;
    const deleting = collections.some(key => next[key].length < current[key].length) || current.projects.some(p => {
      const after = next.projects.find(row => row.id === p.id);
      return after && (after.tasks.length < p.tasks.length || (after.bag?.length || 0) < (p.bag?.length || 0));
    });
    if (deleting && typeof action === "function") setUndo({ data: current, expiresAt: Date.now() + 6000 });
    dataRef.current = next; setStoredData(next);
  };
  const navigate=(to:string)=>{
    const projectId=new URL(to,window.location.origin).searchParams.get("project");
    if(projectId&&dataRef.current.projects.some(project=>project.id===projectId))setData(current=>({...current,activeProjectId:projectId}));
    routeNavigate(to);
  };
  useEffect(() => { if (!undo) return; const timer = window.setTimeout(() => setUndo(undefined), Math.max(0, undo.expiresAt - Date.now())); return () => window.clearTimeout(timer); }, [undo]);
  const [freeHint,setFreeHint]=useState(false);
  const [externalError,setExternalError]=useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [confirmedLocalData, setConfirmedLocalData] = useState<AppData | null>(null);
  const tossStorage = useSyncExternalStore(subscribeTossStorage, getTossStorageState);
  const tossConflict = tossStorage.status === "conflict";
  const [resolvingToss, setResolvingToss] = useState(false);
  const retryRead = () => {
    const next = readAppData();
    setReadState(next);
    if (!next.issue) { setData(next.data); setSaveFailed(false); }
  };
  const persist = () => {
    try { saveAppData(data); setConfirmedLocalData(data); setSaveFailed(false); }
    catch { setSaveFailed(true); }
  };
  const resolveTossCopy = async (choice: "local" | "toss" | "empty") => {
    setResolvingToss(true);
    try {
      const resolved = await resolveTossStorageConflict(choice);
      if (resolved) retryRead();
      return resolved;
    } finally { setResolvingToss(false); }
  };
  useEffect(() => {
    const viewport = window.visualViewport;
    const baselineHeight=window.innerHeight;
    const update = () => document.documentElement.classList.toggle("keyboard-open", Boolean(viewport && Math.max(baselineHeight,window.innerHeight) - viewport.height > 140));
    viewport?.addEventListener("resize", update);
    return () => { viewport?.removeEventListener("resize", update); document.documentElement.classList.remove("keyboard-open"); };
  }, []);
  const mainRef = useRef<HTMLElement>(null);
  const previousPathname = useRef(pathname);
  const online = useOnlineStatus();
  useEffect(() => {
    let flushing=false;
    const flush=async()=>{
      void flushAggregateEvents();
      const snapshot=dataRef.current.analytics;
      if(flushing||!snapshot.consent||!snapshot.queue.length)return;
      flushing=true;
      try {
        const result=await flushAnalytics(apiBase(),snapshot);
        const remaining=new Set(result.queue.map(event=>event.id));
        const sent=new Set(snapshot.queue.filter(event=>!remaining.has(event.id)).map(event=>event.id));
        if(sent.size)setData(current=>({...current,analytics:{...current.analytics,queue:current.analytics.queue.filter(event=>!sent.has(event.id))}}));
      } finally {flushing=false;}
    };
    void flush();window.addEventListener("online",flush);window.addEventListener("pagehide",flush);
    return()=>{window.removeEventListener("online",flush);window.removeEventListener("pagehide",flush);};
  }, [pathname,data.analytics.consent]);
  useEffect(() => {
    if (!isAppsInTossHost()) return;
    const open = (event: MouseEvent) => {
      const anchor=(event.target as Element)?.closest<HTMLAnchorElement>("a[href]");if(!anchor)return;
      const url=new URL(anchor.href,window.location.href);
      if((url.protocol!=="https:"&&url.protocol!=="http:"&&url.protocol!=="mailto:")||url.origin===window.location.origin)return;
      event.preventDefault();void import("@apps-in-toss/web-framework").then(({openURL})=>openURL(url.href)).catch(()=>setExternalError(true));
    };
    document.addEventListener("click",open,true);return()=>document.removeEventListener("click",open,true);
  }, []);
  const publicRoute=["/privacy","/terms","/support"].includes(pathname)||pathname.startsWith("/s/");
  const requestedProjectId = pathname.startsWith("/project/") ? pathname.split("/")[2] : undefined;
  useEffect(() => {
    const attribution = parseAttribution(window.location.search);
    if (!attribution) return;
    setData((current) => current.analytics.attribution ? current : ({ ...current, analytics: { ...current.analytics, attribution } }));
  }, []);
  useEffect(() => {
    const eventName = pathname === "/" ? "landing_view" : pathname === "/discover" ? "recommendation_viewed" : pathname === "/portfolio" ? "portfolio_opened" : pathname === "/plan" ? "offer_viewed" : undefined;
    if (!eventName) return;
    setData((current) => ({ ...current, analytics: enqueueAnalytics(current.analytics, createAnalyticsEvent(eventName, { route: pathname })) }));
  }, [pathname, data.analytics.consent]);
  useEffect(() => {
    if (previousPathname.current === pathname) return;
    previousPathname.current = pathname;
    mainRef.current?.focus({ preventScroll: true });
  }, [pathname]);
  useEffect(() => {
    let unsubscribe: () => void = () => undefined;
    void installAppsInTossBackHandler().then((cleanup) => { unsubscribe = cleanup; });
    return () => unsubscribe();
  }, []);
  useEffect(() => {
    if (readState.issue || tossConflict || resolvingToss) return;
    try { saveAppData(data); setConfirmedLocalData(data); setSaveFailed(false); }
    catch { setSaveFailed(true); }
  }, [data, readState.issue, tossConflict, resolvingToss]);
  useEffect(() => {
    if (requestedProjectId && data.projects.some((project) => project.id === requestedProjectId) && data.activeProjectId !== requestedProjectId) {
      setData((current) => ({ ...current, activeProjectId: requestedProjectId }));
    }
  }, [data.activeProjectId, data.projects, requestedProjectId]);

  const onToggleTask = (projectId: string, taskId: string) => setData((current) => ({ ...current, projects: current.projects.map((project) => project.id === projectId ? toggleTask(project, taskId) : project) }));
  const onCreateManualProject = (mediaTitle: string, characterName: string) => {
    if(data.projects.length>=3){try{if(!localStorage.getItem("haebolcae.project-limit-hint")){setFreeHint(true);localStorage.setItem("haebolcae.project-limit-hint","1");void sendAggregateEvent("offer_viewed",{projectStatus:"project_limit"});}}catch{setFreeHint(true);}}
    const project = createManualProject(mediaTitle, characterName, data.lastPreferences);
    const wardrobe = createProjectWardrobe(project.id);
    setData((current) => ({
      ...current, activeProjectId: project.id, projects: [project, ...current.projects],
      wardrobe: [...wardrobe, ...current.wardrobe],
      analytics: enqueueAnalytics(current.analytics, createAnalyticsEvent("project_created", { projectStatus: "manual_planning", count: current.projects.length + 1 })),
    }));
    recordFirstValue(window.location.search.includes("utm_source=share") ? "share" : "home");
    navigate(`/project/${project.id}`);
  };
  const onPatchProject = (projectId: string, patch: Partial<CosplayProject>) => {
    const before = dataRef.current.projects.find(project => project.id === projectId);
    if (patch.dDay && before && !before.dDay) void sendAggregateEvent("event_dated", { source: "manual" });
    setData(current => ({ ...current, projects: current.projects.map(project => project.id === projectId ? { ...project, ...patch } : project) }));
  };
  const resetData = () => { void deleteAppData().then(() => { setUndo(undefined); setStoredData(loadAppData()); navigate("/"); }).catch(() => setSaveFailed(true)); };
  const onDeleteProject = (id: string) => { setData(current => removeProject(current, id)); navigate("/"); };
  const onCloneProject = (id: string) => { const next = cloneProject(dataRef.current, id); setData(next); navigate(`/project/${next.activeProjectId}`); };
  const requestedProject = data.projects.find((project) => project.id === requestedProjectId) ?? (pathname === "/project/demo" ? activeProject(data) : undefined);

  let view: ReactNode;
  if (pathname.startsWith("/s/")) view = <SharedView key={window.location.href} data={data} setData={setData} navigate={navigate} />;
  else if (pathname === "/discover") view = <DiscoverView data={data} setData={setData} navigate={navigate} />;
  else if (pathname.startsWith("/project/")) view = <ProjectView key={requestedProject?.id} data={data} project={requestedProject} onToggleTask={onToggleTask} onPatchProject={onPatchProject} onDeleteProject={onDeleteProject} onCloneProject={onCloneProject} navigate={navigate} />;
  else if (pathname === "/wardrobe") view = <WardrobeView key={`${data.activeProjectId}-${window.location.search}`} data={data} setData={setData} navigate={navigate} />;
  else if (pathname === "/market") view = <MarketView data={data} setData={setData} navigate={navigate} />;
  else if (pathname === "/calendar") view = <CalendarView data={data} navigate={navigate} />;
  else if (["/events", "/events/2026", "/events/kintex"].includes(pathname)) view = <EventsView data={data} setData={setData} navigate={navigate} />;
  else if (pathname === "/shoot") view = <ShootView data={data} setData={setData} navigate={navigate} />;
  else if (pathname === "/photos") view = <PhotosView data={data} setData={setData} navigate={navigate} />;
  else if (pathname === "/portfolio") view = <PortfolioView data={data} setData={setData} navigate={navigate} />;
  else if (pathname === "/plan") view = <PlanView data={data} navigate={navigate} />;
  else if (pathname === "/settings/data") view = <DataSettingsView data={data} setData={setData} resetData={resetData} />;
  else if (pathname === "/privacy") view = <LegalView kind="privacy" />;
  else if (pathname === "/terms") view = <LegalView kind="terms" />;
  else if (pathname === "/support") view = <><LegalView kind="support" /><SupportForm /></>;
  else if (pathname in discoveryLandings) view = <DiscoveryLandingView pathname={pathname as keyof typeof discoveryLandings} navigate={navigate} />;
  else if (pathname === "/") view = <HomeView data={data} navigate={navigate} onToggleTask={onToggleTask} onCreateProject={onCreateManualProject} />;
  else view = <NotFoundView navigate={navigate} />;

  return (
    <div className={`app-shell${isAppsInTossHost() ? " toss-host" : ""}`}>
      <a className="skip-link" href="#main-content">본문으로 건너뛰기</a>
      {!online && <div className="offline-banner" role="status" data-material-state="offline"><strong>오프라인</strong><span>저장된 프로젝트와 확인해 둔 작품은 계속 쓸 수 있어요. 최신 작품 불러오기와 암호화 백업만 잠시 멈춥니다.</span></div>}
      <header className="site-header">
        <AppLink to="/" navigate={navigate} className="brand"><img className="brand-wordmark" src="/brand/wordmark.svg" alt="해볼캐 코스프레 프로젝트" /></AppLink>
        {!pathname.startsWith("/s/") && <ProjectSwitcher data={data} navigate={navigate} />}
      </header>
      {!pathname.startsWith("/s/") && <BottomTabs pathname={pathname} projectId={activeProject(data)?.id} navigate={navigate} />}
      <main id="main-content" data-route={pathname} ref={mainRef} tabIndex={-1}>
        <TossStorageRecovery state={tossStorage} resolving={resolvingToss} resolve={resolveTossCopy} />
        {(tossStorage.status === "conflict" || resolvingToss) && !publicRoute ? null : readState.issue && !publicRoute ? <StorageRecovery issue={readState.issue} retry={retryRead} /> : <>
          {saveFailed && <section className="storage-recovery" role="alert"><h2>최근 변경을 기기에 저장하지 못했어요</h2><p>화면에는 남아 있지만 앱을 닫으면 사라질 수 있어요. 저장 공간을 확인하거나 현재 기록을 파일로 받아 주세요.</p><div className="data-button-row"><button type="button" className="button" onClick={persist}>저장 다시 시도</button><button type="button" className="button" onClick={() => downloadOriginal(JSON.stringify(data, null, 2), "haebolcae-unsaved.json")}>현재 기록 내려받기</button></div></section>}
          {!saveFailed && confirmedLocalData === data && data.projects.length > 0 && (pathname.startsWith("/project/") || ["/wardrobe", "/events"].includes(pathname)) && <p className="save-receipt" role="status">현재 기록을 이 기기에 저장했어요.</p>}
          {freeHint&&<p role="status">결제가 열리면 준비표 4개부터 PRO예요. 지금은 무료예요. <button type="button" className="button text" onClick={()=>navigate("/plan")}>요금 보기</button><button type="button" className="button text" onClick={()=>setFreeHint(false)}>닫기</button></p>}
          {externalError&&<p role="alert">외부 링크를 열지 못했어요. 링크를 길게 눌러 주소를 복사해 주세요.</p>}
          {view}{pathname==="/"&&<InstallHint/>}<PreparationHandoff data={data} pathname={pathname} navigate={navigate} />
        </>}
      </main>
      {undo && <UndoToast label="항목을" undo={() => { dataRef.current = undo.data; setStoredData(undo.data); setUndo(undefined); }} />}
      <footer><div className="footer-brand"><img src="/mark.svg" alt="" /><div><strong>해볼캐</strong><p>정한 캐릭터를 촬영일까지.</p></div></div><div className="footer-links"><AppLink to="/privacy" navigate={navigate}>개인정보</AppLink><AppLink to="/terms" navigate={navigate}>이용 범위</AppLink><AppLink to={`/support?from=${encodeURIComponent(pathname)}`} navigate={navigate}>도움·신고</AppLink><a href="https://anilist.co/" target="_blank" rel="noreferrer">작품·캐릭터 정보: AniList ↗</a></div><p className="footer-status">내 준비 기록은 이 기기에 저장됩니다.</p></footer>
    </div>
  );
}
